import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import {
  buildRestaurantDetail,
  isPickRow,
  isProfileRow,
  type RestaurantDetail,
} from '../lib/restaurantDetail';
import { generateRestaurantSlug } from '../utils/slugify';

export type { RestaurantDetail } from '../lib/restaurantDetail';

interface UseRestaurantBySlugResult {
  detail: RestaurantDetail | null;
  isLoaded: boolean;
  error: string | null;
}

// Resolves /restaurant/:slug to a single restaurant + the recommenders who
// picked it. The slug is computed from the restaurant name (legacy URL
// pattern preserved for SEO), so we have to scan picks looking for a name
// whose slugify matches. The first match wins; in practice each slug is
// unique because place_id is the canonical identity.
export function useRestaurantBySlug(slug: string | undefined): UseRestaurantBySlugResult {
  const [detail, setDetail] = useState<RestaurantDetail | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) {
      setDetail(null);
      setIsLoaded(true);
      return;
    }

    let mounted = true;
    setIsLoaded(false);

    (async () => {
      const [picksRes, profilesRes] = await Promise.all([
        supabase
          .from('user_picks')
          .select('user_id, name, place_id, cuisine, location, position'),
        supabase
          .from('profiles')
          .select('user_id, slug, display_name'),
      ]);
      if (!mounted) return;

      if (picksRes.error) {
        setError(picksRes.error.message);
        setIsLoaded(true);
        return;
      }
      if (profilesRes.error) {
        setError(profilesRes.error.message);
        setIsLoaded(true);
        return;
      }

      const allPicks = Array.isArray(picksRes.data)
        ? picksRes.data.filter(isPickRow)
        : [];
      const allProfiles = Array.isArray(profilesRes.data)
        ? profilesRes.data.filter(isProfileRow)
        : [];

      // Resolve slug → placeId from the first pick whose name slugifies to
      // the URL slug. Multiple picks of the same place share a name, so any
      // match yields the right place_id.
      const match = allPicks.find((p) => {
        try {
          return generateRestaurantSlug(p.name) === slug;
        } catch {
          return false;
        }
      });

      setDetail(match ? buildRestaurantDetail(allPicks, allProfiles, match.place_id) : null);
      setIsLoaded(true);
      setError(null);
    })();

    return () => {
      mounted = false;
    };
  }, [slug]);

  return { detail, isLoaded, error };
}
